import { useState, useEffect, useCallback } from 'react';
import { X, FileText, Package, Clock, DollarSign, AlertTriangle, CheckCircle, ExternalLink, Link2, Calendar, Layers } from 'lucide-react';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useModal } from '../../contexts/ModalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatEuro } from '../../utils/currency';
import {
  getCampaignDeliverables, getCampaignInvoices, getCampaignTimeline,
  getCampaignInstallments, updateDeliverableProof, updateDeliverablePublishedUrl,
  type CreatorDeliverable, type CreatorInvoice, type CampaignTimelineEvent,
  type CampaignInstallment,
} from '../../services/creatorPortalService';

type Tab = 'overview' | 'deliverables' | 'payments' | 'timeline';

function getPlatformIcon(platform: string | null) {
  const p = (platform || '').toLowerCase();
  if (p.includes('instagram')) return 'IG';
  if (p.includes('tiktok')) return 'TT';
  if (p.includes('youtube')) return 'YT';
  if (p.includes('twitter') || p.includes('x')) return 'X';
  if (p.includes('facebook')) return 'FB';
  return 'OT';
}

function getPlatformColor(platform: string | null) {
  const p = (platform || '').toLowerCase();
  if (p.includes('instagram')) return 'bg-pink-100 text-pink-700';
  if (p.includes('tiktok')) return 'bg-gray-900 text-white';
  if (p.includes('youtube')) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
}

function getDeliverableStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'published') return 'bg-green-100 text-green-700';
  if (s === 'approved') return 'bg-emerald-100 text-emerald-700';
  if (s === 'delivered' || s === 'submitted') return 'bg-yellow-100 text-yellow-700';
  if (s === 'production' || s === 'in production') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
}

function getInvoiceStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'paid') return 'bg-green-100 text-green-700';
  if (s === 'overdue') return 'bg-red-100 text-red-700';
  if (s === 'issued' || s === 'sent') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
}

function getTimelineStatusColor(status: string) {
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'liquidado' || s === 'paid') return 'bg-green-500';
  if (s === 'billing' || s === 'faturação') return 'bg-amber-500';
  if (s === 'in progress' || s === 'production') return 'bg-emerald-500';
  if (s === 'consultation' || s === 'consulta') return 'bg-gray-400';
  return 'bg-gray-300';
}

export function CampaignDetailDrawer() {
  const { t } = useLanguage();
  const { toast } = useModal();
  const { selectedCampaignId, setSelectedCampaignId, campaigns, loadData } = useCreatorPortal();
  const [tab, setTab] = useState<Tab>('overview');
  const [deliverables, setDeliverables] = useState<CreatorDeliverable[]>([]);
  const [invoices, setInvoices] = useState<CreatorInvoice[]>([]);
  const [installments, setInstallments] = useState<CampaignInstallment[]>([]);
  const [timeline, setTimeline] = useState<CampaignTimelineEvent[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [editingProof, setEditingProof] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState('');
  const [editingPublished, setEditingPublished] = useState<string | null>(null);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const campaign = campaigns.find(c => c.id === selectedCampaignId);

  const loadTabData = useCallback(async (tabKey: Tab) => {
    if (!selectedCampaignId) return;
    setLoadingTab(true);
    try {
      if (tabKey === 'deliverables') {
        setDeliverables(await getCampaignDeliverables(selectedCampaignId));
      } else if (tabKey === 'payments') {
        const [inv, inst] = await Promise.all([
          getCampaignInvoices(selectedCampaignId),
          getCampaignInstallments(selectedCampaignId),
        ]);
        setInvoices(inv);
        setInstallments(inst);
      } else if (tabKey === 'timeline') {
        setTimeline(await getCampaignTimeline(selectedCampaignId));
      }
    } catch {
      toast.error(t('creatorsHub.detail.loadError'));
    } finally {
      setLoadingTab(false);
    }
  }, [selectedCampaignId, toast, t]);

  useEffect(() => {
    if (selectedCampaignId) {
      setTab('overview');
      setDeliverables([]);
      setInvoices([]);
      setInstallments([]);
      setTimeline([]);
      getCampaignDeliverables(selectedCampaignId)
        .then(setDeliverables)
        .catch(() => {});
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    if (tab !== 'overview') loadTabData(tab);
  }, [tab, loadTabData]);

  const handleSaveProof = async (deliverableId: string) => {
    if (!proofUrl.trim()) return;
    setSaving(true);
    try {
      await updateDeliverableProof(deliverableId, proofUrl.trim());
      setEditingProof(null);
      setProofUrl('');
      await loadTabData('deliverables');
      loadData();
    } catch {
      toast.error(t('creatorsHub.detail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePublished = async (deliverableId: string) => {
    if (!publishedUrl.trim()) return;
    setSaving(true);
    try {
      await updateDeliverablePublishedUrl(deliverableId, publishedUrl.trim());
      setEditingPublished(null);
      setPublishedUrl('');
      await loadTabData('deliverables');
      loadData();
    } catch {
      toast.error(t('creatorsHub.detail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedCampaignId || !campaign) return null;

  const close = () => setSelectedCampaignId(null);

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: t('creatorsHub.detail.overview'), icon: FileText },
    { key: 'deliverables', label: t('creatorsHub.detail.deliverables'), icon: Package },
    { key: 'payments', label: t('creatorsHub.detail.payments'), icon: DollarSign },
    { key: 'timeline', label: t('creatorsHub.detail.timeline'), icon: Clock },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={close} />
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-900 truncate">{campaign.name}</h2>
            <p className="text-sm text-gray-500 truncate">{campaign.brand}</p>
          </div>
          <button
            onClick={close}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-3"
            aria-label={t('creatorsHub.detail.close')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 bg-white">
          {tabs.map(t => {
            const TabIcon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-accent-500 text-accent-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <TabIcon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loadingTab ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : tab === 'overview' ? (
            <OverviewTab campaign={campaign} deliverables={deliverables} onViewAllDeliverables={() => setTab('deliverables')} />
          ) : tab === 'deliverables' ? (
            <DeliverablesTab
              deliverables={deliverables}
              editingProof={editingProof}
              setEditingProof={setEditingProof}
              proofUrl={proofUrl}
              setProofUrl={setProofUrl}
              editingPublished={editingPublished}
              setEditingPublished={setEditingPublished}
              publishedUrl={publishedUrl}
              setPublishedUrl={setPublishedUrl}
              saving={saving}
              onSaveProof={handleSaveProof}
              onSavePublished={handleSavePublished}
            />
          ) : tab === 'payments' ? (
            <PaymentsTab invoices={invoices} installments={installments} campaign={campaign} />
          ) : (
            <TimelineTab timeline={timeline} />
          )}
        </div>
      </div>
    </>
  );
}

function OverviewTab({ campaign, deliverables, onViewAllDeliverables }: { campaign: any; deliverables: CreatorDeliverable[]; onViewAllDeliverables: () => void }) {
  const { t } = useLanguage();
  const topDeliverables = deliverables.slice(0, 5);
  const hasFees = deliverables.some(d => d.fee && d.fee > 0);

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <InfoBlock label={t('creatorsHub.table.brand')} value={campaign.brand} />
        <InfoBlock label={t('creatorsHub.detail.status')} value={campaign.status} />
        <InfoBlock label={t('creatorsHub.deliverables')} value={`${campaign.deliverables_published}/${campaign.deliverables_count}`} />
        <InfoBlock label={t('creatorsHub.detail.deliveryStatus')} value={campaign.delivery_status} />
        {campaign.start_date && (
          <InfoBlock
            label={t('creatorsHub.detail.deliveryDate')}
            value={new Date(campaign.start_date).toLocaleDateString('pt-PT')}
          />
        )}
        <InfoBlock
          label={t('creatorsHub.detail.paymentStatus')}
          value={campaign.company_invoice_status}
        />
      </div>

      {topDeliverables.length > 0 && (
        <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-900">{t('creatorsHub.detail.deliverablesPortfolio')}</h4>
            </div>
            <span className="text-[11px] font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
              {campaign.deliverables_published}/{campaign.deliverables_count}
            </span>
          </div>
          <div className="space-y-1.5">
            {topDeliverables.map(d => (
              <div key={d.id} className="flex items-center gap-2.5 bg-white rounded-lg px-3 py-2 border border-blue-50">
                <span className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${getPlatformColor(d.platform)}`}>
                  {getPlatformIcon(d.platform)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{d.title}</p>
                  {d.type && <p className="text-[10px] text-gray-400">{d.type}</p>}
                </div>
                {hasFees && d.fee && d.fee > 0 && (
                  <span className="text-[10px] font-semibold text-gray-500 whitespace-nowrap">{formatEuro(d.fee)}</span>
                )}
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded shrink-0 ${getDeliverableStatusBadge(d.status)}`}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
          {deliverables.length > 5 && (
            <button
              onClick={onViewAllDeliverables}
              className="mt-2 w-full text-center text-xs font-medium text-blue-600 hover:text-blue-700 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {t('creatorsHub.detail.viewAll')} ({deliverables.length})
            </button>
          )}
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-5 space-y-3">
        <h4 className="text-sm font-semibold text-gray-900">{t('creatorsHub.detail.financialBreakdown')}</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('creatorsHub.grossFee')}</span>
            <span className="font-medium text-gray-900">{formatEuro(campaign.agreed_fee)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('creatorsHub.commission')} ({(campaign.commission_pct * 100).toFixed(0)}%)</span>
            <span className="font-medium text-red-600">-{formatEuro(campaign.agreed_fee * campaign.commission_pct)}</span>
          </div>
          <div className="border-t border-gray-200 pt-2 flex justify-between">
            <span className="text-sm font-semibold text-gray-900">{t('creatorsHub.netEarnings')}</span>
            <span className="text-lg font-bold text-emerald-600">{formatEuro(campaign.net_earnings)}</span>
          </div>
        </div>
      </div>

      {campaign.amount_received > 0 && (
        <div className="bg-emerald-50 rounded-xl p-5 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">{t('creatorsHub.detail.paymentReceived')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-emerald-700">{t('creatorsHub.detail.amountReceived')}</span>
            <span className="font-bold text-emerald-800">{formatEuro(campaign.amount_received)}</span>
          </div>
          {campaign.influencer_amount_paid > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-emerald-700">{t('creatorsHub.detail.paidToYou')}</span>
              <span className="font-bold text-emerald-800">{formatEuro(campaign.influencer_amount_paid)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

interface DeliverablesTabProps {
  deliverables: CreatorDeliverable[];
  editingProof: string | null;
  setEditingProof: (id: string | null) => void;
  proofUrl: string;
  setProofUrl: (url: string) => void;
  editingPublished: string | null;
  setEditingPublished: (id: string | null) => void;
  publishedUrl: string;
  setPublishedUrl: (url: string) => void;
  saving: boolean;
  onSaveProof: (id: string) => void;
  onSavePublished: (id: string) => void;
}

function DeliverablesTab({
  deliverables, editingProof, setEditingProof, proofUrl, setProofUrl,
  editingPublished, setEditingPublished, publishedUrl, setPublishedUrl,
  saving, onSaveProof, onSavePublished
}: DeliverablesTabProps) {
  const { t } = useLanguage();

  if (deliverables.length === 0) {
    return (
      <div className="p-6 text-center py-16">
        <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-400">{t('creatorsHub.detail.noDeliverables')}</p>
      </div>
    );
  }

  const overdue = deliverables.filter(d => d.is_overdue && d.status !== 'Published');
  const rest = deliverables.filter(d => !d.is_overdue || d.status === 'Published');

  return (
    <div className="p-4 space-y-3">
      {overdue.length > 0 && (
        <div className="bg-red-50 rounded-xl p-3 border border-red-100">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-semibold text-red-700 uppercase tracking-wider">
              {t('creatorsHub.detail.overdueDeliverables')}
            </span>
          </div>
          <div className="space-y-2">
            {overdue.map(d => (
              <DeliverableRow
                key={d.id} d={d}
                editingProof={editingProof} setEditingProof={setEditingProof}
                proofUrl={proofUrl} setProofUrl={setProofUrl}
                editingPublished={editingPublished} setEditingPublished={setEditingPublished}
                publishedUrl={publishedUrl} setPublishedUrl={setPublishedUrl}
                saving={saving} onSaveProof={onSaveProof} onSavePublished={onSavePublished}
              />
            ))}
          </div>
        </div>
      )}
      {rest.map(d => (
        <DeliverableRow
          key={d.id} d={d}
          editingProof={editingProof} setEditingProof={setEditingProof}
          proofUrl={proofUrl} setProofUrl={setProofUrl}
          editingPublished={editingPublished} setEditingPublished={setEditingPublished}
          publishedUrl={publishedUrl} setPublishedUrl={setPublishedUrl}
          saving={saving} onSaveProof={onSaveProof} onSavePublished={onSavePublished}
        />
      ))}
    </div>
  );
}

function DeliverableRow({
  d, editingProof, setEditingProof, proofUrl, setProofUrl,
  editingPublished, setEditingPublished, publishedUrl, setPublishedUrl,
  saving, onSaveProof, onSavePublished
}: {
  d: CreatorDeliverable;
} & Omit<DeliverablesTabProps, 'deliverables'>) {
  const { t } = useLanguage();
  // Deliverables sourced from the campaigns JSONB column have no table row,
  // so proof/published URLs cannot be attached to them.
  const readOnly = d.id.startsWith('jsonb-');
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-7 h-7 rounded-xl flex items-center justify-center text-[10px] font-bold ${getPlatformColor(d.platform)}`}>
            {getPlatformIcon(d.platform)}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{d.title}</p>
            <p className="text-[11px] text-gray-500">
              {d.type && <span>{d.type}</span>}
              {d.due_date && (
                <span className="ml-2">
                  <Calendar className="w-3 h-3 inline mr-0.5" />
                  {new Date(d.due_date).toLocaleDateString('pt-PT')}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {d.fee && d.fee > 0 && (
            <span className="text-[10px] font-semibold text-gray-500">{formatEuro(d.fee)}</span>
          )}
          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-xl whitespace-nowrap ${getDeliverableStatusBadge(d.status)}`}>
            {d.status}
          </span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {d.proof_url ? (
          <a
            href={d.proof_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3" /> {t('creatorsHub.detail.viewProof')}
          </a>
        ) : d.status !== 'Published' && !readOnly ? (
          editingProof === d.id ? (
            <div className="flex items-center gap-1 w-full mt-1">
              <input
                type="url"
                value={proofUrl}
                onChange={e => setProofUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                autoFocus
              />
              <button
                onClick={() => onSaveProof(d.id)}
                disabled={saving || !proofUrl.trim()}
                className="text-xs px-2.5 py-1.5 bg-accent-600 text-white rounded-lg disabled:opacity-50 font-medium transition-transform hover:scale-[1.02]"
              >
                {t('creatorsHub.detail.save')}
              </button>
              <button
                onClick={() => { setEditingProof(null); setProofUrl(''); }}
                className="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700"
              >
                {t('creatorsHub.detail.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setEditingProof(d.id); setProofUrl(d.proof_url || ''); }}
              className="text-[11px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
            >
              <Link2 className="w-3 h-3" /> {t('creatorsHub.detail.addProof')}
            </button>
          )
        ) : null}

        {d.published_url ? (
          <a
            href={d.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-green-600 hover:text-green-700 flex items-center gap-0.5"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3" /> {t('creatorsHub.detail.viewPublished')}
          </a>
        ) : (d.status === 'Published' || d.status === 'Approved') && !readOnly ? (
          editingPublished === d.id ? (
            <div className="flex items-center gap-1 w-full mt-1">
              <input
                type="url"
                value={publishedUrl}
                onChange={e => setPublishedUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                autoFocus
              />
              <button
                onClick={() => onSavePublished(d.id)}
                disabled={saving || !publishedUrl.trim()}
                className="text-xs px-2.5 py-1.5 bg-accent-600 text-white rounded-lg disabled:opacity-50 font-medium"
              >
                {t('creatorsHub.detail.save')}
              </button>
              <button
                onClick={() => { setEditingPublished(null); setPublishedUrl(''); }}
                className="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700"
              >
                {t('creatorsHub.detail.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setEditingPublished(d.id); setPublishedUrl(d.published_url || ''); }}
              className="text-[11px] text-green-600 hover:text-green-700 flex items-center gap-0.5"
            >
              <Link2 className="w-3 h-3" /> {t('creatorsHub.detail.addPublishedUrl')}
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}

function InstallmentProgressBar({ paid, total }: { paid: number; total: number }) {
  const pct = total > 0 ? (paid / total) * 100 : 0;
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          paid === total ? 'bg-green-500' : 'bg-emerald-500'
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function getInstallmentStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'paid') return 'bg-green-100 text-green-700';
  if (s === 'issued' || s === 'sent') return 'bg-blue-100 text-blue-700';
  if (s === 'overdue') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-500';
}

function PaymentsTab({ invoices, installments, campaign }: { invoices: CreatorInvoice[]; installments: CampaignInstallment[]; campaign: any }) {
  const { t } = useLanguage();
  const hasInstallments = installments.length > 1;
  const paidInstallments = installments.filter(i => i.status === 'Paid');

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    return new Date(d + 'T00:00:00').toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="p-6 space-y-4">
      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{t('creatorsHub.netEarnings')}</span>
          <span className="font-semibold text-gray-900">{formatEuro(campaign.net_earnings)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{t('creatorsHub.detail.paidToYou')}</span>
          <span className="font-semibold text-emerald-600">{formatEuro(campaign.influencer_amount_paid)}</span>
        </div>
        <div className="border-t border-gray-200 pt-2 flex justify-between text-sm">
          <span className="text-gray-600 font-medium">{t('creatorsHub.detail.remaining')}</span>
          <span className="font-bold text-gray-900">
            {formatEuro(campaign.net_earnings - campaign.influencer_amount_paid)}
          </span>
        </div>
      </div>

      {hasInstallments && (
        <div className="bg-blue-50 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-blue-900">{t('creatorsHub.detail.installmentProgress')}</h4>
            </div>
            <span className={`text-sm font-bold ${
              paidInstallments.length === installments.length ? 'text-green-600' : 'text-blue-600'
            }`}>
              {paidInstallments.length}/{installments.length}
            </span>
          </div>
          <InstallmentProgressBar paid={paidInstallments.length} total={installments.length} />
          <div className="space-y-2 mt-2">
            {installments.map((inst, idx) => (
              <div key={inst.id} className="flex items-center gap-3 bg-white rounded-lg p-2.5 border border-blue-100">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  inst.status === 'Paid'
                    ? 'bg-green-100 text-green-700'
                    : inst.status === 'Issued'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-900">
                      {inst.invoice_number ? `#${inst.invoice_number}` : `${idx + 1}/${installments.length}`}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded ${getInstallmentStatusBadge(inst.status)}`}>
                      {inst.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                    {inst.issued_date && <span>{formatDate(inst.issued_date)}</span>}
                    {inst.payment_received_date && (
                      <span className="text-green-600">{formatDate(inst.payment_received_date)}</span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                  {formatEuro(inst.amount_received)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length === 0 && !hasInstallments ? (
        <div className="text-center py-8">
          <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{t('creatorsHub.detail.noInvoices')}</p>
        </div>
      ) : invoices.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('creatorsHub.detail.invoiceHistory')}</h4>
          {invoices.map(inv => (
            <div key={inv.id} className="bg-white rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {inv.number || `#${inv.id.slice(0, 6)}`}
                  </span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${getInvoiceStatusBadge(inv.status)}`}>
                    {inv.status}
                  </span>
                </div>
                {inv.total && (
                  <span className="text-sm font-semibold text-gray-900">{formatEuro(inv.total)}</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-gray-500">
                <span className="uppercase">{inv.type}</span>
                {inv.issue_date && <span>{t('creatorsHub.detail.issued')}: {new Date(inv.issue_date).toLocaleDateString('pt-PT')}</span>}
                {inv.due_date && <span>{t('creatorsHub.detail.due')}: {new Date(inv.due_date).toLocaleDateString('pt-PT')}</span>}
              </div>
              {inv.has_tranche_schedule && (
                <div className="mt-2 text-[11px] text-blue-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {inv.tranches_paid_count}/{inv.total_tranches} {t('creatorsHub.detail.tranchesPaid')}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TimelineTab({ timeline }: { timeline: CampaignTimelineEvent[] }) {
  const { t } = useLanguage();

  if (timeline.length === 0) {
    return (
      <div className="p-6 text-center py-16">
        <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-400">{t('creatorsHub.detail.noTimeline')}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="relative">
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />
        <div className="space-y-6">
          {timeline.map((event, idx) => (
            <div key={event.id} className="relative pl-8">
              <div className={`absolute left-1.5 top-1 w-3.5 h-3.5 rounded-full border-2 border-white ${getTimelineStatusColor(event.to_status)}`} />
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-gray-900">{event.to_status}</span>
                  {idx === timeline.length - 1 && (
                    <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {t('creatorsHub.detail.current')}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-500">
                  {new Date(event.created_at).toLocaleDateString('pt-PT', {
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
                {event.reason && (
                  <p className="text-xs text-gray-600 mt-1 italic">{event.reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
